import topApi from '../../components/api/ToPApi';

// Precios por horario (happy hour, menú del día). Reglas en el API: services/price-schedule.ts

export type ScheduleType = 'PCT' | 'PRICE';
export type ScheduleStatus = 'ACT' | 'INA';

export interface ScheduleItem {
    productId: number;
    price: number | null;
}

export interface PriceSchedule {
    id?: number;
    name: string;
    type: ScheduleType;
    value: number;
    days: number[];
    start: string;
    end: string;
    from: string | null;
    to: string | null;
    status?: ScheduleStatus;
    activeNow?: boolean;
    items: ScheduleItem[];
}

export const DAYS = [
    { value: 1, short: 'L', name: 'Lunes' },
    { value: 2, short: 'K', name: 'Martes' },
    { value: 3, short: 'M', name: 'Miércoles' },
    { value: 4, short: 'J', name: 'Jueves' },
    { value: 5, short: 'V', name: 'Viernes' },
    { value: 6, short: 'S', name: 'Sábado' },
    { value: 7, short: 'D', name: 'Domingo' },
];

export const emptySchedule = (): PriceSchedule => ({
    name: '', type: 'PCT', value: 20, days: [1, 2, 3, 4, 5], start: '17:00', end: '19:00', from: null, to: null, items: [],
});

const errorMessage = (e: any) => e?.response?.data?.error || 'Ocurrió un error de comunicación, intente de nuevo.';

export const loadSchedules = async () => {
    try {
        const resp = await topApi.get<{ schedules: PriceSchedule[]; serverTime: string }>('/price-schedules/');
        return resp.data;
    } catch (e) {
        throw new Error(errorMessage(e));
    }
};

export const saveSchedule = async (schedule: PriceSchedule) => {
    const body = { schedule: { ...schedule, status: undefined, activeNow: undefined } };
    try {
        if (schedule.id) await topApi.put(`/price-schedules/${schedule.id}`, body);
        else await topApi.post('/price-schedules/', body);
    } catch (e) {
        throw new Error(errorMessage(e));
    }
};

export const setScheduleStatus = async (id: number, status: ScheduleStatus | 'DEL') => {
    try {
        await topApi.put(`/price-schedules/${id}/status`, { status });
    } catch (e) {
        throw new Error(errorMessage(e));
    }
};

// "Lun a vie, 17:00 a 19:00" / "Sáb y dom, 22:00 a 02:00 (día siguiente)"
export const describeSchedule = (s: Pick<PriceSchedule, 'days' | 'start' | 'end' | 'from' | 'to'>) => {
    const days = [...s.days].sort((a, b) => a - b);
    const name = (d: number) => DAYS[d - 1].name.slice(0, 3).toLowerCase();
    const consecutive = days.length > 2 && days.every((d, i) => i === 0 || d === days[i - 1] + 1);
    const dayText = days.length === 7 ? 'Todos los días'
        : consecutive ? `${name(days[0])} a ${name(days[days.length - 1])}`
            : days.map(name).join(', ');
    const overnight = s.end < s.start ? ' (día siguiente)' : '';
    const range = s.start === s.end ? 'todo el día' : `${s.start} a ${s.end}${overnight}`;
    const dates = s.from || s.to ? ` · ${s.from ?? '…'} al ${s.to ?? '…'}` : '';
    return `${dayText.charAt(0).toUpperCase()}${dayText.slice(1)}, ${range}${dates}`;
};

// Precio que tendría el producto con la regla
export const schedulePrice = (s: Pick<PriceSchedule, 'type' | 'value'>, item: ScheduleItem, catalogPrice: number) =>
    s.type === 'PRICE' ? item.price : Math.round(catalogPrice * (1 - s.value / 100) * 100) / 100;

// Mismas validaciones que el API, para avisar antes de guardar
export const validateSchedule = (s: PriceSchedule): string | null => {
    if (!s.name.trim()) return 'Escriba el nombre del horario.';
    if (s.type === 'PCT' && !(s.value > 0 && s.value < 100)) return 'El descuento debe ser mayor que 0 y menor que 100 %.';
    if (s.days.length === 0) return 'Elija al menos un día.';
    if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(s.start) || !/^([01]\d|2[0-3]):[0-5]\d$/.test(s.end)) return 'Indique la hora de inicio y de fin.';
    if (s.from && s.to && s.to < s.from) return 'La fecha de fin es anterior a la de inicio.';
    if (s.items.length === 0) return 'Elija al menos un producto.';
    if (s.type === 'PRICE' && s.items.some(i => !(Number(i.price) > 0))) return 'Cada producto necesita su precio de horario.';
    return null;
};
